// InsightsWindow — the info-on-hover CONTENT that lives inside the
// Desktop preset's "Insights" floating window (see Dashboard.jsx /
// FloatingWindow). It is NOT a positioned window itself — the
// FloatingWindow chrome owns the title bar, drag, resize, and z-index,
// exactly like every other Desktop window. This component only fills
// that window's body.
//
// Two jobs eventually live here (per hsh-documents/FTUE_PLAN.md): a
// hover/Explain mode (this CC) and a scripted-scene directive mode
// (a later CC). This pass ships hover mode only.
//
// Detection is a pair of document-level delegated listeners on the
// bubbling mouseover/mouseout events (NOT mouseenter/mouseleave —
// those don't bubble, and capture-phase delegation for them is
// unreliable across browsers). A single document listener catches
// movement over any `[data-tname]` element or its descendants
// (resolved via closest()), anywhere on screen, regardless of which
// window it lives in. The relatedTarget guard on mouseout ignores
// moves that stay within the same target.
//
// The shown hint PERSISTS once dwelled — leaving the element does not
// clear it. The window holds the last thing you inspected until you
// dwell on something else or hit close. This is the whole reason it's
// a window and not a cursor tooltip: the manual-section link stays
// reachable, so you can move off the game piece and over to the link
// without the content evaporating mid-reach.

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getTextureHint } from '../../TextureHints';
import './InsightsWindow.css';

// Custom event the manual anchor dispatches; ManualLauncher listens for
// it and opens the in-app manual drawer to the cited section. Exported
// so the listener side imports the same string (no duplicated literal).
export const OPEN_MANUAL_SECTION_EVENT = 'hsh:open-manual-section';

// Scripted-scene FTUE directives flow in over these window events:
// FtueSceneRunner dispatches, this component listens. Mirrors the
// OPEN_MANUAL_SECTION_EVENT custom-event pattern already used here and
// the document-level hover listeners below — one state mechanism, two
// content slots. The push event's `detail` is the directive payload
// ({ message, scene_key, order, is_final, onAdvance }); the clear event
// empties the directive slot. Exported so FtueSceneRunner imports the
// same strings (no duplicated literal).
export const FTUE_DIRECTIVE_EVENT = 'hsh:ftue-directive';
export const FTUE_DIRECTIVE_CLEAR_EVENT = 'hsh:ftue-directive-clear';

// Typed engine Rejects (well-formed click the engine can't honor because
// state moved between send and receive) arrive over this window event:
// PackageRouter dispatches it on a 'Reject'-typed WebSocket message, this
// component listens. Same custom-event mechanism as the directive slot
// above; the `detail` is the verbatim RejectWire payload (handler,
// reason_text, valid_options, ...). Exported so PackageRouter imports the
// same string (no duplicated literal). Reject is engine-driven correction,
// so it takes display priority over the user-driven hover and tutorial
// directive slots.
export const REJECT_MESSAGE_EVENT = 'hsh:reject-message';

// CC_CLICK_NONCE_PROTOCOL Job 4. Rejects from the click-protocol gate
// (missing / duplicate click_uuid) are plumbing-level corrections, not
// game-rule feedback — Michael's read from game 756 was that the raw UUID
// dump was noise and the server logs carried the real signal. So a
// protocol reject renders a ONE-LINE summary, with the full wire detail
// (handler, reason_text, valid_options) tucked into a closed-by-default
// disclosure. Game-rule rejects (faction claimed, illegal dock, …) keep
// full rendering — their valid_options is genuinely actionable.
//
// Match is by handler key: the str() of the CHECK_CLICK_PROTOCOL
// HandlerKey in engine/controls/rejection/action_chain_rejection.py
// ('<module>.<function>'). One constant, no scattered literals.
export const CLICK_PROTOCOL_HANDLER = 'click_router._check_click_protocol';

// COPY PLACEHOLDER — Michael owns this wording.
const CLICK_PROTOCOL_REJECT_SUMMARY =
    'That click could not be processed. Try again.';
const REJECT_DETAILS_LABEL = 'Details';

const DWELL_MS = 700;
const TNAME_SELECTOR = '[data-tname]';

const IDLE_TEXT = 'Hover a game piece for details.';
const PAUSED_TEXT = 'Insights paused — reload to re-enable.';


function dispatchOpenManualSection(section) {
    window.dispatchEvent(new CustomEvent(OPEN_MANUAL_SECTION_EVENT, {
        detail: { section: section },
    }));
}


function parseTname(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const parsed = parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : null;
}


// Render one valid_options value into a brief readable string. The
// container dict is freeform per the BE Reject contract — keys are
// handler-specific and the set grows, so this NEVER enumerates known
// keys. Lists join with commas, shallow nested dicts flatten to
// `key: value` pairs, scalars stringify.
function renderValidOptionValue(value) {
    if (Array.isArray(value)) {
        return value.join(', ');
    }
    if (value !== null && typeof value === 'object') {
        return Object.entries(value)
            .map(([key, nested]) =>
                key + ': ' + renderValidOptionValue(nested))
            .join('; ');
    }
    return String(value);
}


// Format the freeform valid_options dict into a readable summary — one
// labelled line per entry. Generic key/value iteration only; no
// handler-specific keys hardcoded. Returns null for an empty/absent dict
// so the panel renders reason_text alone without an empty list.
function renderValidOptions(payload) {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const entries = Object.entries(payload);
    if (entries.length === 0) {
        return null;
    }
    return (
        <ul className="insights-window-reject-options">
            {entries.map(([key, value]) => (
                <li className="insights-window-reject-option" key={key}>
                    <span className="insights-window-reject-option-key">
                        {key}
                    </span>
                    <span className="insights-window-reject-option-value">
                        {renderValidOptionValue(value)}
                    </span>
                </li>
            ))}
        </ul>
    );
}


// The live gamePlayerData for a seat, resolved from this client's roster
// (game.gamePlayerControllers — one controller per seat, each carrying
// {id, seat, artificial_player, ...}). Null when the roster is absent (the
// unit-test harness with no app) or no seat matches. Read live so the
// once-bound Reject listener resolves it at Reject time through the stable
// app ref.
function findSeatData(app, gamePlayerId) {
    const controllers = (app && app.game
        && app.game.gamePlayerControllers) || [];
    for (const controller of controllers) {
        const data = controller && controller.gamePlayerData;
        if (data && data.id != null
                && Number(data.id) === Number(gamePlayerId)) {
            return data;
        }
    }
    return null;
}


// True when a Reject targets a seat this client auto-drives on behalf of the
// AI (any seat with a non-null artificial_player) rather than the human's
// own manually-played seat. The owner-only FE-AI driver runs every Feai
// seat, so this client RECEIVES their validate-first retry-loop Rejects (the
// Choose Faction ceremony's no_preassigned_faction / faction_already_claimed
// self-correction, G98 2026-07-11) — AI plumbing that must not paint red at
// a human who did nothing. Resolved from the roster, NOT the seated
// controller: seatedController is nulled at launch
// (GameGroupHandler.launchReceiver) and only re-established once the seat's
// data returns, so during the faction ceremony an own-seat check is blind
// and leaks (the seat-0 Feai Reject Michael saw). A Reject whose seat can't
// be resolved, or that resolves to a human seat (artificial_player null),
// still shows — a human's own illegal move needs the actionable feedback.
function rejectIsForDrivenAiSeat(reject, app) {
    const target = reject && reject.game_player;
    if (target === null || target === undefined) {
        return false;
    }
    const seatData = findSeatData(app, target);
    return !!(seatData && seatData.artificial_player != null);
}


// The full wire detail of a reject — handler breadcrumb, reason_text,
// and the valid_options summary. Rendered directly for game-rule rejects
// and inside the collapsed disclosure for click-protocol rejects.
function renderRejectDetail(reject) {
    return (
        <React.Fragment>
            <div className="insights-window-reject-handler">
                {reject.handler}
            </div>
            <div className="insights-window-body">
                {reject.reason_text}
            </div>
            {renderValidOptions(reject.valid_options)}
        </React.Fragment>
    );
}


function InsightsWindow(props) {
    // Default opener dispatches the global event; tests inject a mock.
    const onOpenManualSection = props.onOpenManualSection
        || dispatchOpenManualSection;

    const [hoverHint, setHoverHint] = useState(null);
    const [directive, setDirective] = useState(null);
    const [reject, setReject] = useState(null);
    const [closed, setClosed] = useState(false);

    // Mutable state the once-attached listeners read without re-binding.
    const closedRef = useRef(false);
    const currentTnameRef = useRef(null);
    const dwellTimerRef = useRef(null);
    // Directive slot mirror + a session-kill flag. The once-attached
    // mouseout listener reads directiveRef to decide whether a shown
    // hover hint should persist or yield back to a waiting directive;
    // the directive push listener reads directiveBlockedRef to honor a
    // prior close.
    const directiveRef = useRef(null);
    const directiveBlockedRef = useRef(false);
    // Reject slot mirror. Held in a ref alongside state so the dismiss
    // handler can clear both synchronously, matching the directive slot.
    const rejectRef = useRef(null);
    // Live mirror of the app so the once-bound Reject listener can resolve
    // this human's own seat at Reject time (seatedController may be assigned
    // after mount). Synced each render below.
    const appRef = useRef(null);
    appRef.current = props.app;

    const clearDwell = useCallback(() => {
        if (dwellTimerRef.current !== null) {
            clearTimeout(dwellTimerRef.current);
            dwellTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        function handleOver(event) {
            if (closedRef.current) {
                return;
            }
            const target = event.target;
            if (!target || typeof target.closest !== 'function') {
                return;
            }
            const element = target.closest(TNAME_SELECTOR);
            if (!element) {
                return;
            }
            const tname = parseTname(element.getAttribute('data-tname'));
            if (tname === null) {
                return;
            }
            // Same target (moving across its children, or re-entering the
            // one already shown) — leave the dwell/shown hint alone. Don't
            // restart, or cursor motion inside a card would reset the
            // 700ms timer forever.
            if (currentTnameRef.current === tname) {
                return;
            }
            // New / different target: restart the dwell against it.
            clearDwell();
            currentTnameRef.current = tname;
            dwellTimerRef.current = setTimeout(() => {
                dwellTimerRef.current = null;
                const hint = getTextureHint(tname);
                if (hint) {
                    setHoverHint(hint);
                }
            }, DWELL_MS);
        }

        function handleOut(event) {
            const target = event.target;
            if (!target || typeof target.closest !== 'function') {
                return;
            }
            const element = target.closest(TNAME_SELECTOR);
            if (!element) {
                return;
            }
            // mouseout bubbles on every child-boundary crossing. If the
            // cursor is still inside the same target element, this is an
            // internal move, not a real leave — ignore it.
            const related = event.relatedTarget;
            if (related && typeof element.contains === 'function'
                    && element.contains(related)) {
                return;
            }
            // Real leave: only cancel a dwell that hasn't fired yet, so a
            // hint doesn't pop after you've already moved away. A hint
            // that's already SHOWN persists — the window holds it so you
            // can move over and click the manual link. It's replaced only
            // when you dwell on a different target.
            clearDwell();
            currentTnameRef.current = null;
            // Exception: when a directive is waiting underneath, the
            // hover is only a temporary peek. Releasing it restores the
            // directive, so clear the shown hint on real leave instead of
            // persisting it.
            if (directiveRef.current) {
                setHoverHint(null);
            }
        }

        document.addEventListener('mouseover', handleOver);
        document.addEventListener('mouseout', handleOut);
        return () => {
            document.removeEventListener('mouseover', handleOver);
            document.removeEventListener('mouseout', handleOut);
            clearDwell();
        };
    }, [clearDwell]);

    // Directive slot: scripted-scene steps pushed in by FtueSceneRunner
    // over window events. Same mechanism as the hover slot (a once-bound
    // listener feeding local state) — just a different event source.
    useEffect(() => {
        function handleDirective(event) {
            // A prior close blocks directive pushes for the session.
            if (directiveBlockedRef.current) {
                return;
            }
            const payload = event.detail || null;
            directiveRef.current = payload;
            setDirective(payload);
        }

        function handleDirectiveClear() {
            directiveRef.current = null;
            setDirective(null);
        }

        window.addEventListener(FTUE_DIRECTIVE_EVENT, handleDirective);
        window.addEventListener(
            FTUE_DIRECTIVE_CLEAR_EVENT, handleDirectiveClear);
        return () => {
            window.removeEventListener(FTUE_DIRECTIVE_EVENT, handleDirective);
            window.removeEventListener(
                FTUE_DIRECTIVE_CLEAR_EVENT, handleDirectiveClear);
        };
    }, []);

    // Reject slot: typed engine rejections pushed in by PackageRouter over
    // a window event. Same once-bound listener mechanism as the directive
    // slot. A new Reject replaces any showing one (no queue) — the player
    // only needs the most recent correction. It does NOT auto-dismiss on a
    // timer; the X close button or the next inbound Reject clears it.
    useEffect(() => {
        function handleReject(event) {
            const payload = event.detail || null;
            // Suppress Rejects for the Feai seats the owner auto-drives —
            // they are AI plumbing, not this human's feedback (see
            // rejectIsForDrivenAiSeat). A human seat's Reject still shows.
            const app = appRef.current;
            const seatData = payload
                ? findSeatData(app, payload.game_player) : null;
            // TEMP DIAGNOSTIC (G98 reject leak, 2026-07-11): confirm this
            // seat-scope filter is LIVE in the running bundle and print what
            // it resolves. If a Feai-seat Reject still renders, this line
            // says why — roster empty (stale app), seat not found (id
            // mismatch), or artificial_player null (looked human). Remove
            // once the leak is confirmed shut.
            console.info('[Insights] reject gp=%s artificial_player=%s '
                + 'rosterLen=%s',
                payload && payload.game_player,
                seatData ? seatData.artificial_player : '<no-seat>',
                (app && app.game && app.game.gamePlayerControllers)
                    ? app.game.gamePlayerControllers.length : '<no-roster>');
            if (rejectIsForDrivenAiSeat(payload, app)) {
                return;
            }
            rejectRef.current = payload;
            setReject(payload);
        }

        window.addEventListener(REJECT_MESSAGE_EVENT, handleReject);
        return () => {
            window.removeEventListener(REJECT_MESSAGE_EVENT, handleReject);
        };
    }, []);

    function handleClose() {
        // Direct ref write so the next mouseenter sees the closed state
        // immediately, before React flushes the re-render.
        closedRef.current = true;
        currentTnameRef.current = null;
        clearDwell();
        setHoverHint(null);
        // Close also kills the directive slot and blocks future directive
        // pushes for the session. It deliberately does NOT ack the scene
        // (no FTUE_ACK) — the server still retries next session. Permanent
        // opt-out is the Tutorial Mode toggle (account.ftue), not this.
        directiveBlockedRef.current = true;
        directiveRef.current = null;
        setDirective(null);
        setClosed(true);
    }

    let body;
    if (closed) {
        body = (
            <div className="insights-window-idle">{PAUSED_TEXT}</div>
        );
    } else if (reject) {
        // Engine-driven correction takes priority over the user-driven
        // hover and tutorial directive slots — the player just did
        // something that failed and needs the actionable feedback now.
        //
        // Click-protocol rejects (missing / duplicate click_uuid) are
        // plumbing, not gameplay: render a one-line summary and tuck the
        // wire detail into a closed-by-default disclosure. Game-rule
        // rejects render the full detail directly — their valid_options
        // is genuinely actionable.
        const isProtocolReject = reject.handler === CLICK_PROTOCOL_HANDLER;
        body = (
            <div className="insights-window-panel insights-window-reject">
                <button
                    type="button"
                    className="insights-window-close"
                    aria-label="Dismiss rejection"
                    onClick={() => {
                        rejectRef.current = null;
                        setReject(null);
                    }}
                >
                    ×
                </button>
                {isProtocolReject ? (
                    <React.Fragment>
                        <div className="insights-window-reject-summary">
                            {CLICK_PROTOCOL_REJECT_SUMMARY}
                        </div>
                        <details className="insights-window-reject-details">
                            <summary
                                className="insights-window-reject-details-toggle"
                            >
                                {REJECT_DETAILS_LABEL}
                            </summary>
                            {renderRejectDetail(reject)}
                        </details>
                    </React.Fragment>
                ) : (
                    renderRejectDetail(reject)
                )}
            </div>
        );
    } else if (hoverHint) {
        body = (
            <div className="insights-window-panel">
                <button
                    type="button"
                    className="insights-window-close"
                    aria-label="Close insights"
                    onClick={handleClose}
                >
                    ×
                </button>
                <div className="insights-window-body">
                    {hoverHint.hintText}
                </div>
                {hoverHint.manualSection && (
                    <button
                        type="button"
                        className="insights-window-manual-link"
                        onClick={() =>
                            onOpenManualSection(hoverHint.manualSection)}
                    >
                        {hoverHint.manualSection}
                    </button>
                )}
            </div>
        );
    } else if (directive) {
        // Directive shows only when nothing is being hovered — hover wins
        // display (the player asked for info). The directive stays in
        // state so it restores when the hover clears.
        body = (
            <div className="insights-window-panel">
                <button
                    type="button"
                    className="insights-window-close"
                    aria-label="Close insights"
                    onClick={handleClose}
                >
                    ×
                </button>
                <div className="insights-window-body">
                    {directive.message}
                </div>
                <button
                    type="button"
                    className="insights-window-directive-advance"
                    onClick={() =>
                        directive.onAdvance && directive.onAdvance()}
                >
                    {directive.is_final ? 'Got it' : 'Next'}
                </button>
            </div>
        );
    } else {
        body = (
            <div className="insights-window-idle">{IDLE_TEXT}</div>
        );
    }

    return (
        <div className="insights-window-content">
            {body}
        </div>
    );
}

export default InsightsWindow;
